import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import PasswordInput from "@/components/PasswordInput";
import ThemeToggleFloating from "@/components/ThemeToggleFloating";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const { t } = useLang();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setToken((searchParams.get("token") || "").trim());
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 8) { setErr(t("Wachtwoord moet minstens 8 tekens bevatten")); return; }
    if (password !== confirm) { setErr(t("Wachtwoorden komen niet overeen")); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setOk(true);
      toast.success(t("Wachtwoord bijgewerkt"));
      setTimeout(() => nav("/login"), 2500);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background grid-bg relative">
      <ThemeToggleFloating className="top-4 right-4" />
      <form onSubmit={submit} className="w-full max-w-md bg-card border-2 border-foreground p-8 heavy-shadow">
        <div className="flex justify-center mb-6">
          <div className="bg-[#0b1735] p-3 border-2 border-foreground/20">
            <img
              src="/logo-meerjarenplan.png"
              alt="MeerjarenplanOnline.nl"
              className="h-8 w-auto"
              data-testid="brand-logo"
            />
          </div>
        </div>
        <div className="stat-label mb-2">{t("Toegang · Herstel")}</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter mb-4">{t("Nieuw wachtwoord")}</h1>

        {ok ? (
          <div data-testid="reset-success" className="border-2 border-positive bg-positive/10 text-foreground p-4">
            <p className="inline-flex items-center gap-2 font-bold mb-1"><CheckCircle2 className="w-5 h-5 text-positive" /> {t("Klaar!")}</p>
            <p className="text-sm text-muted-foreground">{t("Je wachtwoord is bijgewerkt. Je wordt zo doorgestuurd naar de inlogpagina.")}</p>
          </div>
        ) : !token ? (
          <div data-testid="reset-no-token" className="border-2 border-destructive/60 bg-destructive/10 text-foreground p-4">
            <p className="font-bold mb-1">{t("Ongeldige link")}</p>
            <p className="text-sm text-muted-foreground">{t("Deze link mist een token. Vraag een nieuwe reset-link aan.")}</p>
            <Link to="/forgot-password" data-testid="link-forgot" className="mt-3 inline-flex items-center gap-1 text-primary font-bold underline underline-offset-2">
              {t("Vraag nieuwe link aan")}
            </Link>
          </div>
        ) : (
          <>
            <label className="stat-label">{t("Nieuw wachtwoord")}</label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              testId="reset-password-1"
              className="mt-1 mb-4"
            />
            <label className="stat-label">{t("Bevestig wachtwoord")}</label>
            <PasswordInput
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              testId="reset-password-2"
              className="mt-1 mb-4"
            />
            {err && <div data-testid="reset-error" className="border-2 border-destructive bg-destructive/10 text-destructive p-3 text-sm mb-4">{err}</div>}
            <button disabled={busy} data-testid="reset-submit"
              className="btn-brutal w-full bg-primary text-primary-foreground py-3 font-display uppercase font-black tracking-wider text-lg disabled:opacity-70">
              {busy ? t("Bezig…") : t("Wachtwoord opslaan")}
            </button>
            <p className="mt-4 text-sm text-muted-foreground">
              <Link to="/login" data-testid="link-login" className="inline-flex items-center gap-1 text-primary font-bold underline underline-offset-2">
                <ArrowLeft className="w-4 h-4" /> {t("Terug naar inloggen")}
              </Link>
            </p>
          </>
        )}
      </form>
    </div>
  );
}
